<template>
  <v-flex>
    <v-row>
      <v-btn
        v-if="!isView"
        @click="downloadAjustment"
        small
        outlined
        class="ml-8 rounded text-capitalize"
        tile
        color="primary"
      >
        <v-icon left> print </v-icon>
        Imprimir
      </v-btn>
      <v-btn
        v-else
        @click="isView = false"
        small
        outlined
        class="ml-8 mb-5 rounded text-capitalize"
        tile
        color="primary"
      >
        <v-icon left> print </v-icon>
        Volver
      </v-btn>
    </v-row>

    <div v-if="isView">
      <iframe
        v-if="pdfUrl"
        style="min-height: 90vh; width: 100%; position: relative"
        :src="pdfUrl"
        frameborder="0"
        allowfullscreen
      ></iframe>
    </div>

    <div v-else>
      <v-container fluid class="pa-4">
        <!-- Header del Documento -->
        <v-card elevation="2" class="document-card mb-6" rounded="lg">
          <v-card-text class="pa-6">
            <div class="text-center mb-4">
              <div class="document-icon-wrapper mb-4">
                <v-icon color="primary" size="36">mdi-package-variant-closed</v-icon>
              </div>
              <h1 class="text-h4 font-weight-bold primary--text mb-2">
                Salida de Inventario
              </h1>
              <h2 class="text-h6 grey--text text--darken-1 font-weight-regular mb-2">
                {{
                  (data_adjustment &&
                    data_adjustment[0] &&
                    data_adjustment[0].ajustment_description) ||
                  ""
                }}
              </h2>
              <h3 class="text-body-1 font-weight-regular grey--text text--darken-1">
                {{
                  (data_adjustment &&
                    data_adjustment[0] &&
                    data_adjustment[0].business_unit_description) ||
                  ""
                }}
              </h3>
            </div>

            <v-divider class="my-4"></v-divider>

            <v-row class="info-grid">
              <v-col cols="12" md="6" lg="3">
                <div class="info-card">
                  <div class="info-icon">
                    <v-icon size="20">mdi-warehouse</v-icon>
                  </div>
                  <div class="info-content">
                    <span class="info-label">Almacén</span>
                    <span class="info-value">
                      {{
                        (data_adjustment &&
                          data_adjustment[0] &&
                          data_adjustment[0].location_description) ||
                        ""
                      }}
                    </span>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="6" lg="3">
                <div class="info-card">
                  <div class="info-icon">
                    <v-icon size="20">mdi-calendar-clock</v-icon>
                  </div>
                  <div class="info-content">
                    <span class="info-label">Fecha</span>
                    <span class="info-value">
                      {{
                        getDate(
                          data_adjustment &&
                            data_adjustment[0] &&
                            data_adjustment[0].effective_date
                        ) || ""
                      }}
                    </span>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="6" lg="3">
                <div class="info-card">
                  <div class="info-icon">
                    <v-icon size="20">mdi-account-tie</v-icon>
                  </div>
                  <div class="info-content">
                    <span class="info-label">Responsable</span>
                    <span class="info-value">
                      {{
                        (data_adjustment &&
                          data_adjustment[0] &&
                          data_adjustment[0].employee_name) ||
                        ""
                      }}
                    </span>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="6" lg="3">
                <div class="info-card">
                  <div class="info-icon">
                    <v-icon size="20">mdi-format-list-bulleted</v-icon>
                  </div>
                  <div class="info-content">
                    <span class="info-label">Artículos</span>
                    <span class="info-value"> {{ parsed_items.length }} item(s) </span>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Sección de Artículos -->
        <v-card elevation="2" class="articles-card mb-6" rounded="lg">
          <v-card-title class="section-header pa-4">
            <v-icon class="mr-3" color="primary">mdi-format-list-bulleted</v-icon>
            <div class="flex-grow-1">
              <h3 class="text-h6 font-weight-bold mb-0">Artículos</h3>
              <p class="text-caption mb-0 grey--text">Lista de productos en la salida</p>
            </div>
          </v-card-title>

          <v-data-table
            :headers="headers"
            :items="parsed_items"
            hide-default-footer
            :items-per-page="1000"
            class="elevation-0 modern-table"
          >
            <template v-slot:item.item_id="{ item }">
              <div class="id-cell">
                <span class="id-badge">{{ item.item_id }}</span>
              </div>
            </template>

            <template v-slot:item.item_name="{ item }">
              <div class="item-name-cell">
                <div class="text-body-2 font-weight-medium">
                  {{ item.item_name }}
                </div>
              </div>
            </template>

            <template v-slot:item.warehouse_name="{ item }">
              <div class="location-cell">
                <v-icon size="14" class="mr-1">mdi-warehouse</v-icon>
                {{ item.warehouse_name }}
              </div>
            </template>

            <template v-slot:item.department_name="{ item }">
              <div class="department-cell">
                <v-icon size="14" class="mr-1">mdi-domain</v-icon>
                {{ item.department_name }}
              </div>
            </template>

            <template v-slot:item.quantity="{ item }">
              <div class="quantity-cell">
                <span class="quantity-badge">{{ item.quantity }}</span>
              </div>
            </template>

            <template v-slot:item.note="{ item }">
              <div class="note-cell">
                <span class="text-caption grey--text">{{ item.note || "-" }}</span>
              </div>
            </template>
          </v-data-table>
        </v-card>

        <!-- Nota General -->
        <v-card
          v-if="data_adjustment && data_adjustment[0] && data_adjustment[0].note"
          elevation="1"
          class="note-card mb-6"
          rounded="lg"
        >
          <v-card-text class="pa-4">
            <div class="d-flex align-start">
              <div class="note-icon mr-3">
                <v-icon size="20">mdi-note-text</v-icon>
              </div>
              <div>
                <h4 class="text-subtitle-1 font-weight-bold mb-2">Nota General</h4>
                <p class="text-body-2 grey--text text--darken-1 mb-0">
                  {{ data_adjustment[0].note }}
                </p>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Sección de Contabilidad -->
        <v-card elevation="2" class="accounting-card mb-6" rounded="lg">
          <v-card-title class="section-header pa-4">
            <v-icon class="mr-3" color="primary">mdi-calculator-variant</v-icon>
            <div class="flex-grow-1">
              <h3 class="text-h6 font-weight-bold mb-0">Contabilidad</h3>
              <p class="text-caption mb-0 grey--text">
                Asientos contables del movimiento
              </p>
            </div>
          </v-card-title>

          <v-data-table
            :headers="headersContabilidad"
            :items="accounting"
            class="elevation-0 modern-table"
            hide-default-footer
            :items-per-page="100"
          >
            <template v-slot:item.diary_book_code="{ item }">
              <span class="account-code">{{ item.diary_book_code }}</span>
            </template>

            <template v-slot:item.chart_of_account_name="{ item }">
              <div class="account-cell">
                <div class="text-caption font-weight-bold primary--text">
                  {{ item.chart_of_account_code }}
                </div>
                <div class="text-caption grey--text text--darken-1">
                  {{ item.chart_of_account_name }}
                </div>
              </div>
            </template>

            <template v-slot:item.location_description="{ item }">
              <span class="location-text">{{ item.location_description }}</span>
            </template>

            <template v-slot:item.entity_name="{ item }">
              <span class="text-caption grey--text text--darken-1">{{
                item.entity_name || "-"
              }}</span>
            </template>

            <template v-slot:item.category_name="{ item }">
              <span v-if="item.category_name" class="category-text">{{
                item.category_name
              }}</span>
              <span v-else class="grey--text text-caption">-</span>
            </template>

            <template v-slot:item.subcategory_name="{ item }">
              <span v-if="item.subcategory_name" class="subcategory-text">{{
                item.subcategory_name
              }}</span>
              <span v-else class="grey--text text-caption">-</span>
            </template>

            <template v-slot:item.debito="{ item }">
              <div class="amount-cell text-right">
                <span v-if="item.entry_type_id == 1" class="debit-amount">
                  {{ currencyFormat(item.amount) }}
                </span>
                <span v-else class="grey--text text-caption">-</span>
              </div>
            </template>

            <template v-slot:item.credito="{ item }">
              <div class="amount-cell text-right">
                <span v-if="item.entry_type_id == 2" class="credit-amount">
                  {{ currencyFormat(item.amount) }}
                </span>
                <span v-else class="grey--text text-caption">-</span>
              </div>
            </template>

            <template slot="body.append">
              <tr class="totals-row">
                <td colspan="6" class="text-right pa-4">
                  <span class="total-label font-weight-bold">TOTAL</span>
                </td>
                <td class="text-right pa-4">
                  <div class="total-amount debit">
                    <span class="amount">{{ currencyFormat(getTotalDebito()) }}</span>
                    <span class="currency">{{
                      accounting.length ? accounting[0].currency_code : ""
                    }}</span>
                  </div>
                </td>
                <td class="text-right pa-4">
                  <div class="total-amount credit">
                    <span class="amount">{{ currencyFormat(getTotalCredito()) }}</span>
                    <span class="currency">{{
                      accounting.length ? accounting[0].currency_code : ""
                    }}</span>
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-container>
    </div>
  </v-flex>
</template>

<script>
import moment from "moment";

export default {
  data: () => ({
    accounting: [],
    storage_invoice: "",
    pdfUrl: null,
    headersContabilidad: [
      {
        text: "Asiento",
        align: "start",
        sortable: false,
        value: "diary_book_code",
        width: "120px",
      },
      {
        text: "Cuenta",
        value: "chart_of_account_name",
        width: "250px",
      },
      {
        text: "Location",
        value: "location_description",
        width: "140px",
      },
      {
        text: "Entidad",
        value: "entity_name",
        width: "100px",
      },
      {
        text: "Categoría",
        value: "category_name",
        width: "110px",
      },
      {
        text: "Sub Categoría",
        value: "subcategory_name",
        width: "120px",
      },
      {
        text: "Débito",
        value: "debito",
        align: "right",
        width: "100px",
      },
      {
        text: "Crédito",
        value: "credito",
        align: "right",
        width: "100px",
      },
    ],
    isView: false,
    id_transactions: null,
    data_adjustment: null,
    parsed_items: [],
    data_transactions_entity: [],
    selectedAttached: "",
    headers: [
      {
        text: "ID",
        align: "start",
        sortable: false,
        value: "item_id",
        width: "80px",
      },
      {
        text: "Artículo",
        value: "item_name",
        width: "300px",
      },
      {
        text: "Ubicación",
        value: "warehouse_name",
        width: "130px",
      },
      {
        text: "Departamento",
        value: "department_name",
        width: "140px",
      },
      {
        text: "Cantidad",
        value: "quantity",
        align: "center",
        width: "100px",
      },
    ],
  }),

  created() {
    this.getData();
  },

  methods: {
    async downloadAjustment() {
      this.loading = true;
      this.isView = true;

      try {
        const responseDataPdf = await this.api_Post("inventory/view_outflow_pdf", {
          id: this.$route.params.id,
        });

        const blob = await responseDataPdf.blob();
        const pdfUrl = URL.createObjectURL(blob);

        this.pdfUrl = pdfUrl;
      } catch (error) {
        console.error("Error loading PDF:", error);
      }

      this.loading = false;
    },

    async getData() {
      this.loading = true;

      try {
        const responseRequest = await this.api_Post("inventory/view_outflow", {
          id: this.$route.params.id,
        });

        if (responseRequest.status == 200) {
          let data = await responseRequest.json();
          this.data_adjustment = data;

          if (data && data.length > 0 && data[0].items_details_json) {
            if (Array.isArray(data[0].items_details_json)) {
              this.parsed_items = data[0].items_details_json;
            }
          }
        } else {
          this.$toast.error("Error al cargar la salida de inventario!");
        }

        const responseAccountig = await this.api_Post("accounting/payment_accounting", {
          filters: `where outflow_id =${this.$route.params.id}`,
        });

        if (responseAccountig.status == 200) {
          this.accounting = await responseAccountig.json();
        }
      } catch (error) {
        console.error("Error loading data:", error);
        this.$toast.error("Error al cargar los datos!");
      }

      this.loading = false;
    },

    parseItems(item) {
      if (!item) return [];
      if (Array.isArray(item)) {
        return item;
      }
      return [];
    },

    getDate(item) {
      return moment(item).format("DD/MM/YYYY");
    },

    getTotalDebito() {
      return this.accounting.reduce((acc, entry) => {
        if (entry.entry_type_id === 1) {
          return acc + parseFloat(entry.amount);
        }
        return acc;
      }, 0);
    },

    getTotalCredito() {
      return this.accounting.reduce((acc, entry) => {
        if (entry.entry_type_id === 2) {
          return acc + parseFloat(entry.amount);
        }
        return acc;
      }, 0);
    },

    currencyFormat(value) {
      return new Intl.NumberFormat("es-DO", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(value);
    },
  },
};
</script>

<style scoped>
/* Layout principal */
.v-container {
  background-color: #f8f9fa;
}

/* Header del documento */
.document-card {
  background: white;
  border: 1px solid #e9ecef;
}

.document-icon-wrapper {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Grid de información */
.info-grid {
  margin: 0 -8px;
}

.info-card {
  display: flex;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
  height: 100%;
}

.info-card:hover {
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.info-icon {
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  color: #6c757d;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.info-content {
  display: flex;
  flex-direction: column;
}

.info-label {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 500;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #343a40;
}

/* Secciones */
.articles-card,
.accounting-card {
  background: white;
  border: 1px solid #e9ecef;
}

.section-header {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

/* Tablas */
.modern-table >>> .v-data-table__wrapper {
  border-radius: 0;
}

.modern-table >>> thead th {
  background: #f8f9fa !important;
  color: #495057 !important;
  font-weight: 600 !important;
  font-size: 0.8rem !important;
  border-bottom: 1px solid #dee2e6 !important;
  padding: 16px 12px !important;
}

.modern-table >>> tbody tr {
  transition: background-color 0.2s ease;
}

.modern-table >>> tbody tr:hover {
  background-color: #f8f9fa !important;
}

.modern-table >>> tbody td {
  padding: 12px !important;
  border-bottom: 1px solid #f0f0f0 !important;
}

/* Celdas específicas */
.id-cell {
  display: flex;
  justify-content: center;
}

.id-badge {
  background: #e9ecef;
  color: #495057;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.item-name-cell {
  padding: 6px 0;
}

.location-cell,
.department-cell {
  display: flex;
  align-items: center;
  color: #6c757d;
  font-size: 0.875rem;
}

.quantity-cell {
  display: flex;
  justify-content: center;
}

.quantity-badge {
  background: #28a745;
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  min-width: 40px;
  text-align: center;
}

.note-cell {
  font-style: italic;
}

/* Contabilidad */
.account-code {
  background: #f8f9fa;
  color: #495057;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.account-cell {
  padding: 6px 0;
}

.location-text,
.category-text,
.subcategory-text {
  font-size: 0.875rem;
  color: #6c757d;
}

.amount-cell {
  font-family: "Roboto Mono", monospace;
}

.debit-amount {
  color: #28a745;
  font-weight: 600;
}

.credit-amount {
  color: #dc3545;
  font-weight: 600;
}

/* Nota */
.note-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-left: 4px solid #007bff;
}

.note-icon {
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #007bff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Totales */
.totals-row {
  background: #f8f9fa !important;
  border-top: 2px solid #dee2e6 !important;
}

.total-label {
  color: #495057;
  font-size: 0.9rem;
}

.total-amount {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.total-amount .amount {
  font-family: "Roboto Mono", monospace;
  font-weight: 600;
  font-size: 0.9rem;
}

.total-amount .currency {
  font-size: 0.7rem;
  color: #6c757d;
  margin-top: 2px;
}

.total-amount.debit .amount {
  color: #28a745;
}

.total-amount.credit .amount {
  color: #dc3545;
}

/* Responsive */
@media (max-width: 960px) {
  .info-card {
    margin-bottom: 8px;
  }

  .modern-table >>> tbody td {
    padding: 8px !important;
    font-size: 0.8rem;
  }

  .modern-table >>> thead th {
    padding: 8px !important;
    font-size: 0.75rem !important;
  }
}

/* Animaciones suaves */
.v-card {
  transition: all 0.2s ease;
}
</style>
